// Cache-Storage-through wrapper for the two large, immutable BUNDLED static
// assets that ORT/emscripten fetch by URL — so they can't go through the
// AAM-scoped cachedFetchAamFile (src/lib/aamAssetCache.js):
//   - the ~25 MB ONNX Runtime wasm  (/onnxruntime/ort-wasm…jsep.wasm)
// (A ~75 MB distilled rig2mesh ONNX used to be the second entry here. That
// decoder is gone — the exact MetaHuman rig replaced it — so the pattern went
// with it. A cached copy on an existing browser is simply never requested again;
// "Clear Cache" drops the whole bucket.)
// A warm load serves both from local storage instead of re-pulling ~100 MB over
// the wire. Installed as a window.fetch wrapper at boot (import side-effect in
// main.jsx), which chains cleanly with gsPack's own fetch patch and assetUrl's.
//
// Keyed by URL, so an entry is exactly as fresh as its URL is specific:
//   - riglogic.wasm is CONTENT-HASHED (it lives in src/, next to its glue, and is
//     emitted as /assets/riglogic-<hash>.wasm), so a re-bake changes the URL and
//     this cache misses on its own. Nothing to remember.
//   - the ORT wasm is copied VERBATIM and UNHASHED out of node_modules into
//     public/onnxruntime/ by a build plugin, and ORT's own glue locates it by
//     name at runtime. That one has no automatic freshness, so an npm bump that
//     changes the binary under the same path needs STATIC_ASSET_VERSION (below,
//     folded into CACHE_NAME) — now the only reason that lever exists.
// GET only, no Range requests, secure-context only; everything else passes
// straight through untouched.

import { cacheFirst } from './cacheFirst.js'

const PATTERNS = [
  /\/onnxruntime\/ort-wasm[^/]*\.wasm(\?|$)/i,
  // OpenRigLogic's wasm — identity-INDEPENDENT code, bundled with the app and
  // fetched by URL, so it belongs here. Matched ONLY in its content-hashed build
  // shape (/assets/riglogic-<hash>.wasm), because the hash is the entire reason
  // this entry needs no manual freshness lever. The dev server serves the same
  // file UNHASHED at /src/lib/orl/riglogic.wasm, so caching that URL would put
  // back exactly the trap moving the file out of public/ removed: re-bake, copy
  // it in, and the dev server keeps handing you the old binary until Clear
  // Cache. Dev is a localhost fetch of 1.5 MB — not worth a stale-binary hunt.
  // NO per-identity rig is cached here and none should be — a baked rig is
  // character data, lives in that character's /ogs bundle, and goes through the
  // AAM-scoped, size-versioned cachedFetchAamFile.
  /\/riglogic-[A-Za-z0-9_-]+\.wasm(\?|$)/i,
]
// Not bumped for the riglogic move: entries cached under the old unhashed
// /orl/riglogic.wasm are simply never requested again, and discarding every
// user's ORT wasm to tidy up a dead key would cost a ~25 MB re-download for
// nothing. (Same reasoning the retired rig2mesh entry got, above.)
//
// v3 was the last of three manual bumps chasing riglogic re-bakes — the failure
// they existed to prevent was NEW content-hashed glue calling into the OLD
// cached binary, missing the exports it expects, for a dead rig recoverable only
// through Clear Cache. Hashing the wasm makes that structural, so this lever is
// left for the ORT wasm alone.
const STATIC_ASSET_VERSION = 'v3' // bump when the ORT wasm changes under the same path
export const CACHE_NAME = `aam-static-${STATIC_ASSET_VERSION}`

// Drop the whole static-asset cache bucket (ORT wasm + riglogic.wasm). Wired
// into the "Clear Cache" recovery button so a stale/corrupt cached binary can't
// survive a clear and keep the avatar broken. Best-effort, never throws.
export async function clearStaticAssetCache() {
  try { if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME) } catch { /* non-fatal */ }
}

// Friendly download-progress label for the boot bar.
function labelFor(url) {
  if (/riglogic\.wasm/i.test(url) || /ort-wasm/i.test(url)) return 'Downloading runtime…'
  return 'Downloading…'
}

// Extract url/method/headers from the (input, init) fetch args, where input may
// be a string or a Request.
function reqInfo(input, init) {
  const isReq = input && typeof input === 'object' && 'url' in input
  const url = typeof input === 'string' ? input : (isReq ? input.url : '')
  const method = (init && init.method) || (isReq && input.method) || 'GET'
  const headers = (init && init.headers) || (isReq ? input.headers : null)
  return { url, method, headers }
}

function hasRange(headers) {
  if (!headers) return false
  if (typeof headers.get === 'function') return !!headers.get('range')
  if (Array.isArray(headers)) return headers.some(([k]) => String(k).toLowerCase() === 'range')
  return Object.keys(headers).some((k) => k.toLowerCase() === 'range')
}

// A GET, non-range request for one of the big bundled assets → cache it.
export function shouldCacheStatic(url, method = 'GET', headers) {
  if (!url || (method && method.toUpperCase() !== 'GET')) return false
  if (hasRange(headers)) return false // range/partial fetch → never cache-serve
  return PATTERNS.some((re) => re.test(url))
}

// Wrap an existing fetch with cache-first behaviour for the big static assets.
// Pure + injectable (origFetch, cacheStorage) so it's unit-testable without the
// browser globals. `cacheStorage` is the Cache Storage API (global `caches`).
export function makeCachingFetch(origFetch, cacheStorage, { cacheName = CACHE_NAME } = {}) {
  return async (input, init) => {
    const { url, method, headers } = reqInfo(input, init)
    if (!shouldCacheStatic(url, method, headers)) return origFetch(input, init)

    let cache
    try { cache = await cacheStorage.open(cacheName) } catch { return origFetch(input, init) }

    return cacheFirst(cache, url, () => origFetch(input, init), {
      id: url,
      label: labelFor(url),
      sizeOf: (r) => Number(r?.headers?.get?.('content-length')) || 0,
      meter: true,
      requireStatus200: true,
    })
  }
}

// Install the wrapper on window.fetch. Idempotent; no-op without Cache Storage
// (insecure origin / SSR).
export function installStaticAssetCache() {
  if (typeof window === 'undefined' || typeof caches === 'undefined') return
  if (window.__STATIC_ASSET_CACHE__) return
  window.__STATIC_ASSET_CACHE__ = true
  window.fetch = makeCachingFetch(window.fetch.bind(window), caches)
}

installStaticAssetCache()
